<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import JuyangAiAPI from 'dashboard/api/juyangAi';

import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  assistantId: { type: Number, required: true },
  editable: { type: Boolean, default: false },
});

const emit = defineEmits(['changed']);
const { t } = useI18n();
const inboxes = ref([]);
const isLoading = ref(false);
const savingIds = ref(new Set());

const errorMessage = error =>
  error?.response?.data?.errors?.join(', ') ||
  error?.response?.data?.error ||
  t('JUYANG_AI.ERROR');

const loadInboxes = async () => {
  isLoading.value = true;
  try {
    const { data } = await JuyangAiAPI.getNested(props.assistantId, 'inboxes');
    inboxes.value = data;
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLoading.value = false;
  }
};

const toggleInbox = async inbox => {
  const nextValue = !inbox.connected;
  savingIds.value = new Set([...savingIds.value, inbox.id]);

  try {
    if (nextValue) {
      await JuyangAiAPI.connectInbox(props.assistantId, inbox.id);
    } else {
      await JuyangAiAPI.disconnectInbox(props.assistantId, inbox.id);
    }
    inbox.connected = nextValue;
    useAlert(t('JUYANG_AI.INBOXES.SUCCESS'));
    emit('changed');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    const ids = new Set(savingIds.value);
    ids.delete(inbox.id);
    savingIds.value = ids;
  }
};

watch(() => props.assistantId, loadInboxes, { immediate: true });
</script>

<template>
  <section class="flex flex-col gap-5">
    <div>
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('JUYANG_AI.INBOXES.TITLE') }}
      </h2>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('JUYANG_AI.INBOXES.DESCRIPTION') }}
      </p>
    </div>

    <div
      v-if="isLoading"
      class="border-y border-n-weak py-12 text-center text-sm text-n-slate-11"
    >
      {{ t('JUYANG_AI.LOADING') }}
    </div>
    <div
      v-else-if="!inboxes.length"
      class="border-y border-n-weak py-12 text-center text-sm text-n-slate-11"
    >
      {{ t('JUYANG_AI.INBOXES.EMPTY') }}
    </div>
    <ul v-else class="m-0 flex list-none flex-col divide-y divide-n-weak p-0">
      <li
        v-for="inbox in inboxes"
        :key="inbox.id"
        class="flex items-center justify-between gap-4 py-4"
      >
        <div class="min-w-0">
          <p class="m-0 truncate text-sm font-medium text-n-slate-12">
            {{ inbox.name }}
          </p>
          <p class="m-0 mt-1 text-xs text-n-slate-10">
            {{ inbox.channel_type }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <span class="text-xs text-n-slate-11">
            {{
              inbox.connected
                ? t('JUYANG_AI.INBOXES.CONNECTED')
                : t('JUYANG_AI.INBOXES.NOT_CONNECTED')
            }}
          </span>
          <Switch
            :model-value="inbox.connected"
            :disabled="!editable || savingIds.has(inbox.id)"
            @change="toggleInbox(inbox)"
          />
        </div>
      </li>
    </ul>
  </section>
</template>
